import { memo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
  Divider,
  List,
  ListItem,
  FormControlLabel,
  Checkbox,
  ImageList,
  ImageListItem,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  Attachment as AttachmentIcon,
} from "@mui/icons-material";
import PropTypes from "prop-types";

// Parse Boolean/YES/NO Utility
// Backend sends "true" or "false" as strings, we display as YES/NO
const parseBooleanOrYesNo = (value) => {
  if (typeof value === "string") {
    const lowerValue = value.toLowerCase().trim();
    // Handle empty string - no selection
    if (lowerValue === "") return null;
    // Handle new format: "true" -> YES, "false" -> NO
    if (lowerValue === "true" || lowerValue === "yes") return "YES";
    if (lowerValue === "false" || lowerValue === "no") return "NO";
  }
  if (typeof value === "boolean") {
    return value ? "YES" : "NO";
  }
  return null;
};

// Parse Confirmation (always returns boolean)
// Backend sends "true" or "false" as strings, we display as checked/unchecked
const parseConfirmation = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const lowerValue = value.toLowerCase();
    return lowerValue === "true" || lowerValue === "yes";
  }
  return false;
};

// Helper to map user IDs to names
const getUserDisplayValue = (rawValue, UsersList, isMulti = false, t) => {
  if (!rawValue) return t("observationDetails.na");

  if (isMulti) {
    if (!Array.isArray(rawValue)) {
      return JSON.stringify(rawValue) || t("observationDetails.na");
    }
    return (
      rawValue
        .map((userId) => {
          const user = UsersList.find((u) => u.value === userId);
          return user ? user.label : userId;
        })
        .join(", ") || t("observationDetails.na")
    );
  }

  const user = UsersList.find((u) => u.value === rawValue);
  return user ? user.label : rawValue || t("observationDetails.na");
};

const TimelineSection = memo(
  ({ historySteps, convertUTCToLocal, onFileClick, UsersList, t }) => {
    const theme = useTheme();
    const [expandedSteps, setExpandedSteps] = useState({});

    const handleToggleStep = (stepId) => {
      setExpandedSteps((prev) => ({
        ...prev,
        [stepId]: !prev[stepId],
      }));
    };

    return (
      <Card
        elevation={2}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          bgcolor: "background.paper",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            borderColor: "success.main",
            boxShadow: 4,
          },
        }}
      >
        <Box
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            px: { xs: 2.5, sm: 3, md: 4 },
            py: { xs: 2, sm: 2.5 },
            bgcolor: (theme) => `${theme.palette.success.main}08`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AssignmentIcon
              sx={{
                color: "success.main",
                fontSize: { xs: 20, sm: 22 },
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                fontSize: { xs: "1rem", sm: "1.125rem" },
              }}
            >
              {t("observationDetails.actionsTimelineTitle")}
            </Typography>
          </Box>
        </Box>
        <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
          {historySteps.length > 0 ? (
            <Timeline
              sx={{
                m: 0,
                p: 0,
                "& .MuiTimelineItem-root": {
                  minHeight: "auto",
                  "&:before": {
                    display: "none",
                  },
                },
                "& .MuiTimelineSeparator-root": {
                  mt: "4px",
                  mr: 3,
                },
                "& .MuiTimelineContent-root": {
                  py: 1,
                  px: 0,
                  width: "100%",
                },
              }}
            >
              {historySteps.map((step, index) => (
                <TimelineItem key={step.step_id}>
                  <TimelineSeparator>
                    <TimelineDot color="primary" />
                    {index < historySteps.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent>
                    <Card
                      elevation={1}
                      sx={{
                        mb: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.paper",
                        cursor: "pointer",
                        borderRadius: 2,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: "primary.main",
                          transform: "translateY(-1px)",
                          boxShadow: 3,
                        },
                      }}
                      onClick={() => handleToggleStep(step.step_id)}
                    >
                      <CardContent
                        sx={{
                          p: { xs: 2, sm: 2.5 },
                          "&:last-child": { pb: { xs: 2, sm: 2.5 } },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 600 }}
                            >
                              {t("observationDetails.stepStatus", {
                                status: step.status,
                                firstName: step.first_name,
                                lastName: step.last_name,
                              })}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <CalendarIcon fontSize="small" />
                              {convertUTCToLocal(step.created_at)}
                            </Typography>
                          </Box>
                          <IconButton size="small">
                            {expandedSteps[step.step_id] ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        </Box>
                        <Collapse in={expandedSteps[step.step_id]}>
                          {step.step_input_values &&
                          step.step_input_values.length > 0 ? (
                            <Box sx={{ mt: 2 }}>
                              <Divider
                                sx={{
                                  mb: 2,
                                  borderColor:
                                    theme.palette.checklist.border.subtle,
                                }}
                              />
                              <List dense sx={{ pt: 0 }}>
                                {step.step_input_values.map((input) => (
                                  <ListItem
                                    key={input.id}
                                    sx={{ py: 0.5, px: 0 }}
                                  >
                                    {input.input_type === "BOOLEAN" ? (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 2,
                                          width: "100%",
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: 600 }}
                                        >
                                          {input.name}:
                                        </Typography>
                                        <ToggleButtonGroup
                                          value={parseBooleanOrYesNo(
                                            input.raw_value
                                          )}
                                          exclusive
                                          size="small"
                                          sx={{
                                            "& .MuiToggleButton-root": {
                                              px: 2,
                                              py: 0.5,
                                              textTransform: "none",
                                              fontSize: "0.875rem",
                                              fontWeight: 600,
                                              minWidth: 50,
                                              border: `1px solid ${theme.palette.divider}`,
                                              "&.Mui-selected": {
                                                fontWeight: 700,
                                                "&[value='YES']": {
                                                  bgcolor:
                                                    theme.palette.success.main,
                                                  color:
                                                    theme.palette.success
                                                      .contrastText,
                                                  "&:hover": {
                                                    bgcolor:
                                                      theme.palette.success
                                                        .dark,
                                                  },
                                                },
                                                "&[value='NO']": {
                                                  bgcolor:
                                                    theme.palette.error.main,
                                                  color:
                                                    theme.palette.error
                                                      .contrastText,
                                                  "&:hover": {
                                                    bgcolor:
                                                      theme.palette.error.dark,
                                                  },
                                                },
                                              },
                                              "&.Mui-disabled": {
                                                border: `1px solid ${theme.palette.divider}`,
                                              },
                                            },
                                          }}
                                          disabled
                                        >
                                          <ToggleButton
                                            value="YES"
                                            aria-label="yes"
                                          >
                                            {t("observationDetails.yes", "YES")}
                                          </ToggleButton>
                                          <ToggleButton
                                            value="NO"
                                            aria-label="no"
                                          >
                                            {t("observationDetails.no", "NO")}
                                          </ToggleButton>
                                        </ToggleButtonGroup>
                                      </Box>
                                    ) : input.input_type === "CONFIRMATION" ? (
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={parseConfirmation(
                                              input.raw_value
                                            )}
                                            disabled
                                            color="primary"
                                            size="small"
                                            sx={{
                                              "&.Mui-checked": {
                                                color:
                                                  theme.palette.success.main,
                                              },
                                            }}
                                          />
                                        }
                                        label={
                                          <Typography variant="body2">
                                            {input.name}
                                          </Typography>
                                        }
                                      />
                                    ) : input.input_type === "FILE_UPLOAD" ? (
                                      <Box sx={{ width: "100%" }}>
                                        <Typography
                                          variant="body2"
                                          sx={{ mb: 1, fontWeight: 500 }}
                                        >
                                          <AttachmentIcon
                                            sx={{
                                              fontSize: 16,
                                              mr: 0.5,
                                              verticalAlign: "middle",
                                            }}
                                          />
                                          {input.name}:
                                        </Typography>
                                        {input.attachments &&
                                        input.attachments.length > 0 ? (
                                          <ImageList
                                            cols={3}
                                            rowHeight={120}
                                            sx={{
                                              width: "100%",
                                              height: "auto",
                                              mt: 1,
                                            }}
                                          >
                                            {input.attachments.map(
                                              (attachment) => (
                                                <ImageListItem
                                                  key={`${attachment.id}-${index}`}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    onFileClick(attachment.url);
                                                  }}
                                                  sx={{
                                                    cursor: "pointer",
                                                    borderRadius: 1,
                                                    overflow: "hidden",
                                                    border: `1px solid ${theme.palette.checklist.border.subtle}`,
                                                    "&:hover": {
                                                      borderColor:
                                                        theme.palette.checklist
                                                          .border.focus,
                                                      transform: "scale(1.02)",
                                                    },
                                                    transition:
                                                      "all 0.2s ease-in-out",
                                                  }}
                                                >
                                                  {attachment.is_image ? (
                                                    <img
                                                      src={attachment.url}
                                                      alt={
                                                        attachment.display_name ||
                                                        t(
                                                          "observationDetails.imageAlt"
                                                        )
                                                      }
                                                      loading="lazy"
                                                      style={{
                                                        objectFit: "cover",
                                                        width: "100%",
                                                        height: 100,
                                                      }}
                                                    />
                                                  ) : (
                                                    <Box
                                                      sx={{
                                                        textAlign: "center",
                                                        pt: 2,
                                                        bgcolor:
                                                          theme.palette
                                                            .checklist.surface
                                                            .subtle,
                                                        height: 100,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                          "center",
                                                      }}
                                                    >
                                                      <Avatar
                                                        src="/file-icon.png"
                                                        sx={{
                                                          width: 32,
                                                          height: 32,
                                                          bgcolor:
                                                            theme.palette
                                                              .checklist.badge
                                                              .background,
                                                        }}
                                                        alt={
                                                          attachment.display_name ||
                                                          t(
                                                            "observationDetails.fileAlt"
                                                          )
                                                        }
                                                      />
                                                    </Box>
                                                  )}
                                                  <Box
                                                    sx={{
                                                      p: 1,
                                                      bgcolor:
                                                        theme.palette.checklist
                                                          .surface.elevated,
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="caption"
                                                      noWrap
                                                    >
                                                      {attachment.display_name ||
                                                        t(
                                                          "observationDetails.fileLabel"
                                                        )}
                                                    </Typography>
                                                  </Box>
                                                </ImageListItem>
                                              )
                                            )}
                                          </ImageList>
                                        ) : (
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            {t(
                                              "observationDetails.noFilesUploaded"
                                            )}
                                          </Typography>
                                        )}
                                      </Box>
                                    ) : [
                                        "MULTI_USER_SELECTION",
                                        "USER_SELECTION",
                                      ].includes(input.input_type) ? (
                                      <Typography variant="body2">
                                        <strong>{input.name}:</strong>{" "}
                                        {getUserDisplayValue(
                                          input.raw_value,
                                          UsersList,
                                          input.input_type ===
                                            "MULTI_USER_SELECTION",
                                          t
                                        )}
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2">
                                        <strong>{input.name}:</strong>{" "}
                                        {input.input_type === "TIMESTAMP"
                                          ? convertUTCToLocal(input.raw_value)
                                          : input.input_type === "NUMERIC"
                                            ? input.raw_value
                                            : input.input_type ===
                                                  "MULTI_SELECTION" &&
                                                Array.isArray(input.raw_value)
                                              ? input.raw_value.join(", ")
                                              : input.input_type ===
                                                    "SELECTION" ||
                                                  input.input_type ===
                                                    "SHORT_TEXT"
                                                ? input.raw_value
                                                : typeof input.raw_value ===
                                                    "object"
                                                  ? JSON.stringify(
                                                      input.raw_value
                                                    )
                                                  : input.raw_value ||
                                                    t("observationDetails.na")}
                                      </Typography>
                                    )}
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                mt: 2,
                                color: "text.secondary",
                                fontStyle: "italic",
                              }}
                            >
                              {t("observationDetails.noInputsForStep")}
                            </Typography>
                          )}
                        </Collapse>
                      </CardContent>
                    </Card>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
                bgcolor: "action.hover",
                borderRadius: 2,
                border: "2px dashed",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: "action.selected",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <AssignmentIcon
                  sx={{
                    fontSize: 32,
                    color: "text.secondary",
                  }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  color: "text.primary",
                  mb: 1,
                  fontWeight: 600,
                  fontSize: { xs: "1rem", sm: "1.125rem" },
                }}
              >
                {t("observationDetails.noCommentsAvailable")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                {t("observationDetails.noActionsYet")}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }
);

TimelineSection.displayName = "TimelineSection";

TimelineSection.propTypes = {
  historySteps: PropTypes.array.isRequired,
  convertUTCToLocal: PropTypes.func.isRequired,
  onFileClick: PropTypes.func.isRequired,
  UsersList: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
};

export default TimelineSection;
